import React, { useState } from 'react';
import { Theme } from '../../../../Enums/Theme.enum';
import PatientManualRegistrationModal from './PatientManualRegistrationModal';
import { useAppContext } from '../../../../Services/AppContext';
import Button from '../../../../Components/Button/Button';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import useTranslation from '../../../../Hooks/useTranslation';
import FlyerPrintButtonAfterRegistration from '../FlyerPrint/FlyerPrintButton/FlyerPrintButtonAfterRegistration';
import { PatientManualRegistrationParams } from './PatientManualRegistration';
import { Size } from '../../../../Enums/Size.enum';
import OverlayLoading from '../../../../Components/OverlayLoading';
import useScope from '../../../Scope/Hooks/useScope';

interface Props {
	reloadData?: () => void;
	buttonClassName?: string;
}

const PatientManualRegistrationButton: React.FC<Props> = ({
	reloadData,
	buttonClassName,
}) => {
	const { t } = useTranslation();
	const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
	const [patientId, setPatientId] = useState<string | null>(null);
	const [parentId, setParentId] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [
		isConfirmationWithoutInviteModalOpen,
		setIsConfirmationWithoutInviteModalOpen,
	] = useState(false);
	const [
		isConfirmationWithInviteModalOpen,
		setIsConfirmationWithInviteModalOpen,
	] = useState(false);
	const { workplaceId, redirectArchive } = useAppContext();
	const { isKDARWorkplace } = useScope();

	const handleAfterRegistrationSuccess = (
		params: PatientManualRegistrationParams,
	) => {
		if (params.ignoreActions) {
			setIsRegistrationModalOpen(false);
			reloadData?.();
		} else {
			setPatientId(params.patientId);
			setParentId(params.parentId);
			setIsRegistrationModalOpen(false);
			if (params.withInvite) {
				setIsConfirmationWithInviteModalOpen(true);
			} else {
				setIsConfirmationWithoutInviteModalOpen(true);
			}
		}
	};

	const handleCloseConfirmation = () => {
		setIsConfirmationWithInviteModalOpen(false);
		setIsConfirmationWithoutInviteModalOpen(false);
		reloadData?.();
	};

	if (isLoading) {
		return <OverlayLoading />;
	}

	return (
		<>
			<Button
				onClick={() => setIsRegistrationModalOpen(true)}
				title='doctor.centralEvidenceScreen.registerNewPatient'
				theme={Theme.GREEN}
				className={buttonClassName}
			/>
			<PatientManualRegistrationModal
				workplaceId={workplaceId}
				onClose={() => setIsRegistrationModalOpen(false)}
				onSuccess={handleAfterRegistrationSuccess}
				isOpened={isRegistrationModalOpen}
			/>
			<ConfirmationModal
				title='informationMessage.patientsManualRegistrationSuccess.title'
				text={
					t('informationMessage.patientsManualRegistrationSuccess.text') ?? ''
				}
				imageSource='/dist/icons/v2/check-green.svg'
				isOpen={isConfirmationWithoutInviteModalOpen}
				onClose={handleCloseConfirmation}
				size={Size.LG}>
				<>
					{patientId && (
						<FlyerPrintButtonAfterRegistration
							patientId={patientId}
							parentId={parentId}
						/>
					)}
				</>
			</ConfirmationModal>
			<ConfirmationModal
				title={
					'informationMessage.patientsManualRegistrationWithInviteSuccess.' +
					(isKDARWorkplace ? 'titleKDAR' : 'title')
				}
				text={
					t(
						'informationMessage.patientsManualRegistrationWithInviteSuccess.' +
							(isKDARWorkplace ? 'textKDAR' : 'text'),
					) ?? ''
				}
				imageSource='/dist/icons/v2/check-green.svg'
				isOpen={isConfirmationWithInviteModalOpen}
				onClose={handleCloseConfirmation}
				size={Size.LG}>
				<>
					{patientId && (
						<FlyerPrintButtonAfterRegistration
							patientId={patientId}
							parentId={parentId}
						/>
					)}
				</>
			</ConfirmationModal>
		</>
	);
};

export default PatientManualRegistrationButton;
